import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadCloud, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";

const PACKAGES = [
  { id: "mix_20", name: "20 Kanala Kadar", tag: "1 – 20 stem", price: 8000 },
  { id: "mix_40", name: "40 Kanala Kadar", tag: "21 – 40 stem", price: 12000 },
  { id: "mix_40plus", name: "40+ Kanal (Sınırsız)", tag: "41+ stem", price: 15000 },
];

export default function OrderPage() {
  const nav = useNavigate();
  const [pkg, setPkg] = useState("mix_40");
  const [withMaster, setWithMaster] = useState(false);
  const [trackName, setTrackName] = useState("");
  const [channels, setChannels] = useState(24);
  const [notes, setNotes] = useState("");
  const [order, setOrder] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const total = (PACKAGES.find((p) => p.id === pkg)?.price || 0) + (withMaster ? 5000 : 0);

  const createOrder = async () => {
    if (!trackName.trim()) {
      toast.error("Şarkı adı gerekli");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/orders", {
        package_id: pkg,
        with_master: withMaster,
        track_name: trackName,
        channels: Number(channels) || 0,
        notes,
      });
      setOrder(r.data);
      toast.success("Sipariş oluşturuldu");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sipariş oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const uploadFile = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    const r = await api.post(`/orders/${order.order_id}/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data;
  };

  const handleFiles = async (fileList) => {
    if (!order) return;
    setBusy(true);
    try {
      for (const f of Array.from(fileList)) {
        const rec = await uploadFile(f);
        setFiles((prev) => [...prev, rec]);
      }
      toast.success("Dosyalar yüklendi");
    } catch (e) {
      toast.error("Yükleme hatası");
    } finally {
      setBusy(false);
    }
  };

  const goPay = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", { order_id: order.order_id, kind: "initial" });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ödeme başlatılamadı");
      setBusy(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16" data-testid="order-page">
      <div className="mono-caps mb-3">Sipariş Ver</div>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">Mix & Master paketini seç</h1>

      <div className="grid md:grid-cols-12 gap-8 mt-10">
        <div className="md:col-span-8 space-y-6">
          {/* Package cards */}
          <div className="grid md:grid-cols-3 gap-4">
            {PACKAGES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPkg(p.id)}
                data-testid={`pkg-${p.id}`}
                className={`panel text-left p-6 transition ${pkg === p.id ? "glow-cyan" : "panel-hover"}`}
              >
                <div className="mono-caps">{p.tag}</div>
                <div className="font-display text-lg mt-1 text-white">{p.name}</div>
                <div className="font-display text-3xl mt-4 text-white">{p.price.toLocaleString("tr-TR")} <span className="text-sm text-slate-400">₺</span></div>
              </button>
            ))}
          </div>

          {/* Master + details */}
          <div className="panel p-6 space-y-4">
            <label className="flex items-center justify-between cursor-pointer" data-testid="master-toggle">
              <div>
                <div className="font-display text-white">Analog Mastering Ekle</div>
                <div className="text-sm text-slate-400">Yayına hazır loudness ve renk. +5.000 ₺</div>
              </div>
              <input type="checkbox" checked={withMaster} onChange={(e) => setWithMaster(e.target.checked)} className="w-5 h-5 accent-cyan-400" />
            </label>
          </div>

          <div className="panel p-6 space-y-4">
            <div>
              <label className="mono-caps">Şarkı Adı</label>
              <input value={trackName} onChange={(e) => setTrackName(e.target.value)}
                data-testid="track-name-input"
                className="mt-2 w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
            </div>
            <div>
              <label className="mono-caps">Kanal Sayısı</label>
              <input type="number" min="1" value={channels} onChange={(e) => setChannels(e.target.value)}
                data-testid="channels-input"
                className="mt-2 w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
            </div>
            <div>
              <label className="mono-caps">Notlar</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
                placeholder="Referans şarkılar, istediğin karakter, önemli notlar…"
                data-testid="notes-input"
                className="mt-2 w-full bg-transparent border border-white/10 rounded-lg px-3 py-2 text-white focus:border-cyan-400 outline-none" />
            </div>
            {!order && (
              <button onClick={createOrder} disabled={busy} data-testid="create-order-btn"
                className="btn-cyan disabled:opacity-50">Sipariş Oluştur</button>
            )}
          </div>

          {order && (
            <div className="panel p-6" data-testid="upload-panel">
              <div className="mono-caps">Ses Dosyalarını Yükle</div>
              <h3 className="font-display text-xl text-white mt-1">Stem/multitrack dosyalarını buraya bırak</h3>
              <label className="mt-4 border-2 border-dashed border-white/10 rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer hover:border-cyan-400/50 transition">
                <UploadCloud className="w-10 h-10 text-cyan-300" />
                <div className="text-white mt-3">Tıkla veya sürükle · WAV, AIFF, ZIP, RAR</div>
                <div className="text-xs text-slate-500 mt-1 font-mono uppercase tracking-widest">Emergent Object Storage</div>
                <input type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)}
                  data-testid="file-input" />
              </label>
              {files.length > 0 && (
                <ul className="mt-4 space-y-2" data-testid="uploaded-list">
                  {files.map((f) => (
                    <li key={f.file_id} className="flex items-center gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      <span>{f.original_filename}</span>
                      <span className="ml-auto text-slate-500 font-mono">{(f.size / 1e6).toFixed(1)} MB</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Summary */}
        <aside className="md:col-span-4">
          <div className="panel p-6 sticky top-24" data-testid="order-summary">
            <div className="mono-caps">Özet</div>
            <div className="mt-4 flex justify-between text-slate-300">
              <span>{PACKAGES.find((p) => p.id === pkg)?.name}</span>
              <span>{PACKAGES.find((p) => p.id === pkg)?.price.toLocaleString("tr-TR")} ₺</span>
            </div>
            {withMaster && (
              <div className="mt-2 flex justify-between text-slate-300">
                <span>Mastering</span><span>5.000 ₺</span>
              </div>
            )}
            <div className="mt-2 flex justify-between text-slate-500 text-sm">
              <span>Revizyon (2 ücretsiz)</span><span>Dahil</span>
            </div>
            <div className="border-t border-white/10 my-4" />
            <div className="flex justify-between items-baseline">
              <span className="mono-caps">Toplam</span>
              <span className="font-display text-3xl font-bold text-white">{total.toLocaleString("tr-TR")} <span className="text-sm text-slate-400">₺</span></span>
            </div>
            {order ? (
              <button onClick={goPay} disabled={busy || files.length === 0}
                data-testid="pay-btn"
                className="btn-cyan w-full mt-6 disabled:opacity-40">
                {files.length === 0 ? "Önce dosya yükle" : "Ödemeye Geç"}
              </button>
            ) : (
              <div className="text-xs text-slate-500 mt-6 text-center">Önce sipariş oluştur, sonra dosya yükle & öde.</div>
            )}
            <div className="text-xs text-slate-500 mt-4 font-mono uppercase tracking-widest text-center">Stripe · Güvenli Ödeme</div>
          </div>
        </aside>
      </div>
    </main>
  );
}
